import { FlexRow } from '@/components/common/Flex';
import {
    Box,
    FlexProps,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
} from '@chakra-ui/react';
import { Goal } from '@common/goals';
import { useCurrentRateInfo } from '@common/react/rates';
import { useCurrentUser } from '@common/react/user';
import { TransactionSchedulePlan } from '@common/types';
import { toPx } from '@common/util/styles';
import { isNumber } from '@common/util/types';
import { useState } from 'react';
import { BigParagraph } from '../../common/Typography';
import { GoalCardStack } from '../cardStack/GoalCardStack';
import { configByGoalType } from '../config';
import GoalProgressCircle from '../GoalProgressCircle';
import GoalProgressPills from '../GoalProgressPills';
import {
    getGoalCardIndexFromFieldName,
    getGoalDataFromExistingGoal,
    goalGuidanceCardHeight,
    goalGuidanceCardWidth,
} from '../helpers';
import { useGoalDetailContext } from './GoalDetailContext';

export interface GoalStatementSectionProps extends FlexProps {
    riskScore?: number | null;
    initialDepositPlan?: number;
    transactionSchedulePlan?: TransactionSchedulePlan;
    hideProgress?: boolean;
    isReadOnlyIfTargetDateIsDynamic?: boolean;
    onUpdate?: (goal: Goal) => void;
}

const GoalStatementSection = ({
    riskScore,
    transactionSchedulePlan,
    initialDepositPlan,
    hideProgress,
    isReadOnlyIfTargetDateIsDynamic,
    onUpdate,
    ...props
}: GoalStatementSectionProps) => {
    const { userInfo } = useCurrentUser();
    const { currentRateInfo } = useCurrentRateInfo();
    const { goal } = useGoalDetailContext();

    const { isGoalStatementPopoverOpen, setIsGoalStatementPopoverOpen } =
        useGoalDetailContext();
    const [initialFrameIndex, setInitialFrameIndex] = useState<number | null>(
        null
    );

    if (!currentRateInfo || !goal) return null;

    const config = configByGoalType[goal.type];

    const goalData = getGoalDataFromExistingGoal(goal);
    // Override stored goal data with supplied props (if defined)
    if (isNumber(riskScore)) {
        goalData.riskScore = riskScore;
    }
    if (isNumber(initialDepositPlan)) {
        goalData.initialDepositPlan = initialDepositPlan;
    }
    if (transactionSchedulePlan) {
        goalData.transactionSchedulePlan = transactionSchedulePlan;
    }

    if (isGoalStatementHidden(goal)) {
        return null;
    }

    const progress =
        goal &&
        config.getProgress({
            goal: goal,
            userInfo,
            currentRateInfo,
        });

    const goalStatementContent = config.getGoalStatementContent({
        userInfo,
        currentRateInfo,
        goalData,
        onGoalStatementClick: (fieldName) => {
            const idx = getGoalCardIndexFromFieldName(
                config.inputSteps,
                fieldName
            );
            if (isNumber(idx)) {
                setInitialFrameIndex(idx);
                setIsGoalStatementPopoverOpen(true);
            }
        },
        isReadOnlyIfTargetDateIsDynamic,
    });

    if (!goalStatementContent) return null;

    return (
        <FlexRow minH={22} {...props} alignItems="center">
            {!hideProgress &&
                (goal.targetDate ? (
                    <GoalProgressCircle
                        progress={progress ?? 0}
                        hideProgress={progress === null}
                        type={goal.type}
                        mr={8}
                    />
                ) : goal ? (
                    <GoalProgressPills
                        goal={goal}
                        mr={8}
                        width={toPx(150)}
                        showContributions
                    />
                ) : (
                    <></>
                ))}
            <Popover
                isOpen={isGoalStatementPopoverOpen}
                onClose={() => setIsGoalStatementPopoverOpen(false)}
                closeOnBlur={false}
                isLazy
            >
                <PopoverTrigger>
                    <FlexRow
                        onClick={(e) => {
                            e.preventDefault();
                        }}
                    >
                        <BigParagraph color="textMuted">
                            {goalStatementContent}
                        </BigParagraph>
                    </FlexRow>
                </PopoverTrigger>
                <Box position="relative" zIndex={291}>
                    <PopoverContent width="auto">
                        <PopoverArrow />
                        <PopoverBody
                            minW={toPx(goalGuidanceCardWidth)}
                            w={toPx(goalGuidanceCardWidth)}
                            h={toPx(goalGuidanceCardHeight)}
                            p={0}
                        >
                            {isNumber(initialFrameIndex) && (
                                <GoalCardStack
                                    goalId={goal.id}
                                    config={configByGoalType[goal.type]}
                                    goal={goal}
                                    goalType={goal.type}
                                    initialFrameIndex={initialFrameIndex}
                                    confirmationButtonText={'Update'}
                                    onClose={() => {
                                        setIsGoalStatementPopoverOpen(false);
                                        setInitialFrameIndex(null);
                                    }}
                                    onComplete={(goal) => onUpdate?.(goal)}
                                />
                            )}
                        </PopoverBody>
                    </PopoverContent>
                </Box>
            </Popover>
        </FlexRow>
    );
};

export const isGoalStatementHidden = (goal: Goal): boolean => {
    // FIXME: hiding goal statement entirely for untargeted  goals,
    // currently, since our goal statement is not designed to handle them
    // correctly yet.
    return !goal.targetDate;
};

export default GoalStatementSection;
